import React from "react"
import styled from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import Backdrop from "@components/Backdrop"
import Form from "./Form"
import SizeSelector from "./SizeSelector"
import LanguageSelector from "./LanguageSelector"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import { CartItem } from "@redux/types"
import {
  getDecodedShopifyProductId,
  formatContentfulVariant,
} from "@utils/index"
import _ from "lodash-es"

const openProps = `
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ isOpen?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  opacity: 0;
  transform: translateX(100%);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  cursor: default;
  ${({ isOpen }) => (isOpen ? openProps : "")}

  @media ${breakpoints.tablet} {
    max-width: 64%;
  }

  @media ${breakpoints.laptop} {
    max-width: calc(5 / 12 * 100vw);
  }
`

interface VariantSelectorProps {
  isOpen: boolean
  product: any
  availableVariants: any
  showPromoToast?: boolean
  addToCart: any
  onRequestClose: () => void
}

const VariantSelector: React.FC<VariantSelectorProps> = ({
  isOpen,
  product,
  availableVariants,
  showPromoToast,
  addToCart,
  onRequestClose,
}) => {
  const menuRef: any = React.useRef(null)

  useModalBackgroundClose({
    modalRef: menuRef,
    isOpen,
    onRequestClose: onRequestClose,
  })

  const [languageSelectorIsOpen, setLanguageSelectorIsOpen] =
    React.useState(false)
  const [languageSelectorData, setLanguageSelectorData] = React.useState([])

  const openLanguageSelector = (productLanguages) => {
    setLanguageSelectorData(productLanguages)
    setLanguageSelectorIsOpen(true)
  }

  const closeLanguageSelector = () => {
    setLanguageSelectorIsOpen(false)
  }

  const [sizeSelectorIsOpen, setSizeSelectorIsOpen] = React.useState(false)
  const [sizeSelectorData, setSizeSelectorData] = React.useState([])

  const openSizeSelector = (productSizes) => {
    setSizeSelectorData(productSizes)
    setSizeSelectorIsOpen(true)
  }

  const closeSizeSelector = () => {
    setSizeSelectorIsOpen(false)
  }

  const { onAddProductToCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart } = useElevarEvents()

  const formatProduct = (p) => {
    const formattedVariants = p.variants.map(formatContentfulVariant)
    const variantsWithClickHandlers = formattedVariants.map((v) => {
      const onAddToCart = (quantity) => {
        let multipleDevices
        const productDevices = p.devices || []

        if (productDevices.length > 0) {
          multipleDevices = productDevices.reduce((acc, curr) => {
            if (acc) {
              acc = acc + `, ${curr.name}`
            } else {
              acc = curr.name
            }

            return acc
          }, undefined)
        }

        const imageSrc = _.get(v, "images[0]", "")
        const [cleanedSrc] = imageSrc.split("?")

        addToCart({
          cartProduct: {
            contentfulProductId: p.contentful_id,
            contentfulVariantId: v.contentfulVariantId,
            shopifyProductId: p.shopifyProduct?.shopifyProductId,
            shopifyVariantId: v.variantId,
            productSubType: p.productSubType?.slug,
            productSlug: p.slug,
            productName: p.productName,
            variantName: v.name,
            device: multipleDevices ? multipleDevices : p.device?.name,
            deviceType: p.device?.type?.name,
            brand: p.brand?.name,
            builtInMagnet: !!p.builtInMagnet,
            language: p.language,
            color: v.color,
            size: v.size,
            sku: v.sku,
            price: v.price,
            compareAtPrice: v.compareAtPrice,
            marketPrices: v.marketPrices,
            image: `https:${cleanedSrc}`,
            quantity,
          },
          showPromoToast: showPromoToast || false,
        })

        const elevarProduct: ElevarProductProps = {
          sku: v.sku,
          productId: p.shopifyProduct?.shopifyProductId,
          variantId: v.variantId,
          productName: p.productName,
          variantName: v.name,
          image: `https:${cleanedSrc}`,
          price: v.price,
          brand: "Urban Armor Gear",
          device: multipleDevices ? multipleDevices : p.device?.name,
          deviceType: p.device?.type?.name,
          quantity: String(quantity),
          list: "upsell",
        }

        elevarOnAddToCart(elevarProduct, "upsell")

        const gtmProduct: GTMProductProps = {
          productId: p.shopifyProduct?.shopifyProductId,
          productName: p.productName,
          variantName: v.name,
          sku: v.sku,
          price: v.price,
          brand: p.brand.name,
          device: multipleDevices ? multipleDevices : p.device.name,
          deviceType: p.device?.type?.name,
          quantity,
        }

        onAddProductToCart(gtmProduct)
        onRequestClose()
      }

      return {
        ...v,
        onAddToCart,
      }
    })

    return {
      ...p,
      variants: variantsWithClickHandlers,
    }
  }

  const formattedProduct = React.useMemo(() => {
    return formatProduct(product)
  }, [product])

  return (
    <Backdrop isOpen={isOpen}>
      <Container ref={menuRef} isOpen={isOpen}>
        <Form
          productName={formattedProduct.productName}
          variants={formattedProduct.variants}
          availableVariants={availableVariants}
          onRequestClose={onRequestClose}
          onRequestCloseLanguageSelector={closeLanguageSelector}
          onRequestCloseSizeSelector={closeSizeSelector}
          onClickLanguageSelector={openLanguageSelector}
          onClickSizeSelector={openSizeSelector}
        />
        <LanguageSelector
          isOpen={languageSelectorIsOpen}
          languages={languageSelectorData}
          onRequestClose={closeLanguageSelector}
        />
        <SizeSelector
          isOpen={sizeSelectorIsOpen}
          sizes={sizeSelectorData}
          onRequestClose={closeSizeSelector}
        />
      </Container>
    </Backdrop>
  )
}

export default VariantSelector
